import React from "react";
import {
  makeStyles,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Button,
  FormControlLabel,
  Switch,
  Divider
} from "@material-ui/core";
import { DeleteOutline, Add } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import MessageMediaConfig from "./MessageMediaConfig";
import ButtonNodeConfig from "./ButtonNodeConfig";

const useStyles = makeStyles(theme => ({
  root: {
    width: 300,
    borderLeft: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    overflowY: "auto",
    flexShrink: 0,
    padding: theme.spacing(1.5)
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  field: {
    marginTop: theme.spacing(1)
  },
  row: {
    display: "flex",
    gap: 4,
    alignItems: "flex-start",
    marginTop: theme.spacing(0.5)
  },
  empty: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2)
  }
}));

const uid = prefix =>
  `${prefix}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const FlowNodeConfig = ({ node, onChange, onDelete }) => {
  const classes = useStyles();

  if (!node) {
    return (
      <div className={classes.root}>
        <Typography className={classes.title}>
          {i18n.t("chatFlows.editor.config")}
        </Typography>
        <Typography className={classes.empty} variant="body2">
          {i18n.t("chatFlows.editor.selectNode")}
        </Typography>
      </div>
    );
  }

  const data = node.data || {};
  const type = node.type;

  const patch = partial => {
    onChange({ ...data, ...partial });
  };

  const setRow = (sIdx, rIdx, field, value) => {
    const sections = JSON.parse(JSON.stringify(data.sections || []));
    sections[sIdx].rows[rIdx][field] = value;
    patch({ sections });
  };

  const addRow = () => {
    const sections = JSON.parse(
      JSON.stringify(data.sections || [{ title: "Opções", rows: [] }])
    );
    if (!sections[0]) sections[0] = { title: "Opções", rows: [] };
    sections[0].rows.push({
      id: uid("row"),
      title: `Item ${sections[0].rows.length + 1}`,
      description: ""
    });
    patch({ sections });
  };

  const removeRow = rIdx => {
    const sections = JSON.parse(JSON.stringify(data.sections || []));
    if (!sections[0]) return;
    sections[0].rows.splice(rIdx, 1);
    patch({ sections });
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>
        {i18n.t(`chatFlows.nodes.${type}`)}
      </Typography>
      <TextField
        className={classes.field}
        label={i18n.t("chatFlows.editor.label")}
        value={data.label || ""}
        onChange={e => {
          const value = e.target.value;
          patch({ label: value });
        }}
        variant="outlined"
        size="small"
        fullWidth
      />

      {type === "message" && <MessageMediaConfig data={data} patch={patch} />}
      {type === "audio" && (
        <MessageMediaConfig
          data={{ ...data, mediaType: "audio" }}
          patch={partial =>
            patch({ ...partial, mediaType: "audio" })
          }
          lockedMediaType="audio"
        />
      )}
      {type === "video" && (
        <MessageMediaConfig
          data={{ ...data, mediaType: "video" }}
          patch={partial =>
            patch({ ...partial, mediaType: "video" })
          }
          lockedMediaType="video"
        />
      )}

      {(type === "list" || type === "ai_reply") && (
        <TextField
          className={classes.field}
          label={i18n.t("chatFlows.editor.text")}
          value={data.text || ""}
          onChange={e => {
            const value = e.target.value;
            patch({ text: value });
          }}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
        />
      )}

      {type === "button" && data.buttonMode !== "cta" && (
        <TextField
          className={classes.field}
          label={i18n.t("chatFlows.editor.text")}
          value={data.text || ""}
          onChange={e => {
            const value = e.target.value;
            patch({ text: value });
          }}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
        />
      )}

      {type === "wait_reply" && (
        <>
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.waitPrompt")}
            value={data.prompt || data.text || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ prompt: value, text: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            helperText={i18n.t("chatFlows.editor.waitPromptHint")}
          />
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.variableName")}
            value={data.variableName || "lastReply"}
            onChange={e => {
              const value = e.target.value;
              patch({ variableName: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
        </>
      )}

      {type === "input" && (
        <>
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.prompt")}
            value={data.prompt || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ prompt: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.variableName")}
            value={data.variableName || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ variableName: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
        </>
      )}

      {type === "button" && (
        <ButtonNodeConfig data={data} patch={patch} />
      )}

      {type === "list" && (
        <>
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.listButton")}
            value={data.buttonText || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ buttonText: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
          {((data.sections && data.sections[0] && data.sections[0].rows) || []).map(
            (r, idx) => (
              <div key={r.id || idx} className={classes.row}>
                <TextField
                  label="ID"
                  value={r.id}
                  onChange={e => {
                    const value = e.target.value;
                    setRow(0, idx, "id", value);
                  }}
                  variant="outlined"
                  size="small"
                  style={{ width: 80 }}
                />
                <TextField
                  label="Title"
                  value={r.title}
                  onChange={e => {
                    const value = e.target.value;
                    setRow(0, idx, "title", value);
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <IconButton size="small" onClick={() => removeRow(idx)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </div>
            )
          )}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={addRow}
            style={{ marginTop: 8 }}
          >
            {i18n.t("chatFlows.editor.addRow")}
          </Button>
        </>
      )}

      {type === "condition" && (
        <>
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.leftOperand")}
            value={data.leftOperand || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ leftOperand: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
            helperText="{{lastInput}} ou {{var}}"
          />
          <TextField
            className={classes.field}
            select
            label={i18n.t("chatFlows.editor.operator")}
            value={data.operator || "equals"}
            onChange={e => {
              const value = e.target.value;
              patch({ operator: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          >
            {[
              "equals",
              "not_equals",
              "contains",
              "not_contains",
              "starts_with",
              "greater_than",
              "less_than"
            ].map(op => (
              <MenuItem key={op} value={op}>
                {op}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.rightOperand")}
            value={data.rightOperand || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ rightOperand: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
        </>
      )}

      {type === "delay" && (
        <TextField
          className={classes.field}
          type="number"
          label={i18n.t("chatFlows.editor.seconds")}
          value={data.seconds != null ? data.seconds : 5}
          onChange={e => {
            const value = Number(e.target.value);
            patch({ seconds: value });
          }}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ min: 1, max: 3600 }}
        />
      )}

      {type === "api_call" && (
        <>
          <TextField
            className={classes.field}
            select
            label="Method"
            value={data.method || "GET"}
            onChange={e => {
              const value = e.target.value;
              patch({ method: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
          </TextField>
          <TextField
            className={classes.field}
            label="URL"
            value={data.url || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ url: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
          <TextField
            className={classes.field}
            label="Body JSON"
            value={data.bodyTemplate || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ bodyTemplate: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            className={classes.field}
            label="Response path"
            value={data.responsePath || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ responsePath: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
          <TextField
            className={classes.field}
            label="Save as"
            value={data.saveAs || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ saveAs: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
          />
        </>
      )}

      {type === "end" && (
        <FormControlLabel
          className={classes.field}
          control={
            <Switch
              checked={Boolean(data.handoff)}
              onChange={e => {
                const checked = e.target.checked;
                patch({ handoff: checked });
              }}
              color="primary"
            />
          }
          label={i18n.t("chatFlows.editor.handoff")}
        />
      )}

      {type !== "start" && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Button
            color="secondary"
            startIcon={<DeleteOutline />}
            onClick={onDelete}
            fullWidth
          >
            {i18n.t("chatFlows.editor.deleteNode")}
          </Button>
        </>
      )}
    </div>
  );
};

export default FlowNodeConfig;
